import * as React from 'react';
import {
  UtensilsCrossed,
  Plus,
  X,
  Search,
  ArrowRightLeft,
  Split,
  Loader2,
  RefreshCw,
  Users,
  Clock,
  Trash2,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Table {
  id: string;
  number: number;
  label: string | null;
  capacity: number | null;
  status: 'livre' | 'ocupada' | 'reservada' | 'inativa';
  current_card_id: string | null;
}

interface CardItem {
  id: string;
  card_id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  notes: string | null;
}

interface ConsumptionCard {
  id: string;
  table_id: string;
  table_number: number | null;
  customer_name: string | null;
  status: 'aberto' | 'fechado' | 'cancelado';
  subtotal: number;
  discount: number;
  total: number;
  notes: string | null;
  opened_at: string;
  items: CardItem[];
}

interface Product {
  id: string;
  name: string;
  sale_price: number;
  barcode?: string;
}

interface PaymentEntry {
  method: 'dinheiro' | 'cartao_debito' | 'cartao_credito' | 'pix';
  amount: number;
  received_amount?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

const STATUS_COLORS: Record<string, string> = {
  livre: 'border-green-500 bg-green-500/10 hover:bg-green-500/20',
  ocupada: 'border-red-500 bg-red-500/10 hover:bg-red-500/20',
  reservada: 'border-yellow-500 bg-yellow-500/10 hover:bg-yellow-500/20',
  inativa: 'border-gray-500 bg-gray-500/10 opacity-50',
};

const STATUS_LABELS: Record<string, string> = {
  livre: 'Livre',
  ocupada: 'Ocupada',
  reservada: 'Reservada',
  inativa: 'Inativa',
};

// ─── Main Component ───────────────────────────────────────────────────────────

export function BarRestaurantPage() {
  const [tables, setTables] = React.useState<Table[]>([]);
  const [selectedTable, setSelectedTable] = React.useState<Table | null>(null);
  const [activeCard, setActiveCard] = React.useState<ConsumptionCard | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [cardLoading, setCardLoading] = React.useState(false);

  // Modals
  const [showCreateTable, setShowCreateTable] = React.useState(false);
  const [showProductSearch, setShowProductSearch] = React.useState(false);
  const [showPayment, setShowPayment] = React.useState(false);
  const [showTransfer, setShowTransfer] = React.useState(false);
  const [showSplit, setShowSplit] = React.useState(false);
  const [showOpenCard, setShowOpenCard] = React.useState(false);
  const [editingTable, setEditingTable] = React.useState<{id:string;number:number;label:string;capacity:number;status:string}|null>(null);
  const [deletingTable, setDeletingTable] = React.useState<{id:string;number:number}|null>(null);

  // ─── Data Fetching ──────────────────────────────────────────────────────

  const fetchTables = React.useCallback(async () => {
    try {
      const res = await apiFetch('/api/bar/tables');
      if (res.ok) {
        const data = await res.json();
        setTables(data);
      }
    } catch {
      toast.error('Erro ao carregar mesas');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCard = React.useCallback(async (cardId: string) => {
    setCardLoading(true);
    try {
      const res = await apiFetch(`/api/bar/cards/${cardId}`);
      if (res.ok) {
        const data = await res.json();
        setActiveCard(data);
      }
    } catch {
      toast.error('Erro ao carregar cartão');
    } finally {
      setCardLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // ─── Table Actions ──────────────────────────────────────────────────────

  function handleTableClick(table: Table) {
    setSelectedTable(table);
    if (table.status === 'ocupada' && table.current_card_id) {
      fetchCard(table.current_card_id);
    } else if (table.status === 'livre') {
      setActiveCard(null);
      setShowOpenCard(true);
    } else {
      setActiveCard(null);
    }
  }

  async function handleOpenCard(customerName: string) {
    if (!selectedTable) return;
    try {
      const res = await apiFetch('/api/bar/cards', {
        method: 'POST',
        body: JSON.stringify({
          table_id: selectedTable.id,
          customer_name: customerName || undefined,
        }),
      });
      if (res.ok) {
        const card = await res.json();
        toast.success('Cartão aberto com sucesso');
        setShowOpenCard(false);
        await fetchTables();
        await fetchCard(card.id);
        setSelectedTable((prev) => prev ? { ...prev, status: 'ocupada', current_card_id: card.id } : null);
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao abrir cartão');
      }
    } catch {
      toast.error('Erro ao abrir cartão');
    }
  }

  async function handleRemoveItem(itemId: string) {
    if (!activeCard) return;
    try {
      const res = await apiFetch(`/api/bar/cards/${activeCard.id}/items/${itemId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Item removido');
        await fetchCard(activeCard.id);
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao remover item');
      }
    } catch {
      toast.error('Erro ao remover item');
    }
  }

  async function handleCancelCard() {
    if (!activeCard) return;
    if (!confirm('Tem certeza que deseja cancelar este cartão?')) return;
    try {
      const res = await apiFetch(`/api/bar/cards/${activeCard.id}/cancel`, { method: 'POST' });
      if (res.ok) {
        toast.success('Cartão cancelado');
        setActiveCard(null);
        setSelectedTable(null);
        await fetchTables();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao cancelar cartão');
      }
    } catch {
      toast.error('Erro ao cancelar cartão');
    }
  }

  const handleEditTable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTable) return;
    try {
      const response = await apiFetch(`/api/bar/tables/${editingTable.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ number: editingTable.number, label: editingTable.label || undefined, capacity: editingTable.capacity || undefined, status: editingTable.status }),
      });
      if (!response.ok) { const err = await response.json(); toast.error(err.error || 'Erro ao editar mesa'); return; }
      toast.success('Mesa atualizada');
      setEditingTable(null);
      fetchTables();
    } catch { toast.error('Erro de conexão'); }
  };

  const handleDeleteTable = async () => {
    if (!deletingTable) return;
    try {
      const response = await apiFetch(`/api/bar/tables/${deletingTable.id}`, { method: 'DELETE' });
      if (!response.ok && response.status !== 204) { const err = await response.json(); toast.error(err.error || 'Erro ao excluir mesa'); return; }
      toast.success(`Mesa ${deletingTable.number} excluída`);
      setDeletingTable(null);
      fetchTables();
    } catch { toast.error('Erro de conexão'); }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col md:flex-row">
      {/* Left: Table Map */}
      <div className="flex-1 p-4 md:p-6 overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <UtensilsCrossed className="h-6 w-6" />
              Bar / Restaurante
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {tables.filter((t) => t.status === 'ocupada').length} de {tables.filter((t) => t.status !== 'inativa').length} mesas ocupadas
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchTables}>
              <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
            </Button>
            <Button size="sm" onClick={() => setShowCreateTable(true)}>
              <Plus className="h-4 w-4 mr-1" /> Nova Mesa
            </Button>
          </div>
        </div>

        {/* Status Legend */}
        <div className="flex flex-wrap gap-3 mb-4 text-xs">
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-green-500" /> Livre</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-red-500" /> Ocupada</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-yellow-500" /> Reservada</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-gray-500" /> Inativa</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : tables.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <UtensilsCrossed className="h-12 w-12 mb-3 opacity-40" />
            <p>Nenhuma mesa cadastrada</p>
            <Button className="mt-4" onClick={() => setShowCreateTable(true)}>
              <Plus className="h-4 w-4 mr-1" /> Criar primeira mesa
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {tables.map((table) => (
              <div key={table.id} className="relative group">
              <button
                onClick={() => handleTableClick(table)}
                disabled={table.status === 'inativa'}
                className={`w-full relative flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all ${STATUS_COLORS[table.status]} ${
                  selectedTable?.id === table.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                }`}
              >
                <span className="text-2xl font-bold text-foreground">{table.number}</span>
                {table.label && (
                  <span className="text-xs text-muted-foreground mt-0.5 truncate max-w-full">{table.label}</span>
                )}
                <span className="text-[10px] font-medium mt-1 uppercase tracking-wider text-muted-foreground">
                  {STATUS_LABELS[table.status]}
                </span>
                {table.capacity && (
                  <span className="absolute top-1 right-1.5 text-[10px] text-muted-foreground flex items-center gap-0.5">
                    <Users className="h-2.5 w-2.5" />{table.capacity}
                  </span>
                )}
              </button>
              <div className="absolute top-1 left-1 hidden group-hover:flex gap-0.5 z-10">
                <button onClick={(e) => { e.stopPropagation(); setEditingTable({id:table.id,number:table.number,label:table.label||'',capacity:table.capacity||0,status:table.status}); }} className="rounded bg-blue-500/90 p-1 text-white text-[10px] hover:bg-blue-600" title="Editar">✎</button>
                <button onClick={(e) => { e.stopPropagation(); setDeletingTable({id:table.id,number:table.number}); }} className="rounded bg-red-500/90 p-1 text-white text-[10px] hover:bg-red-600" title="Excluir">✕</button>
              </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right: Active Card Panel */}
      <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-border bg-card/50 flex flex-col overflow-hidden">
        {activeCard ? (
          <ActiveCardPanel
            card={activeCard}
            onAddItem={() => setShowProductSearch(true)}
            onRemoveItem={handleRemoveItem}
            onClose={() => setShowPayment(true)}
            onTransfer={() => setShowTransfer(true)}
            onSplit={() => setShowSplit(true)}
            onCancel={handleCancelCard}
            loading={cardLoading}
          />
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 p-6 text-muted-foreground">
            <UtensilsCrossed className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm text-center">
              {selectedTable
                ? selectedTable.status === 'livre'
                  ? 'Mesa livre — clique para abrir cartão'
                  : 'Selecione uma mesa ocupada'
                : 'Selecione uma mesa para ver o cartão'}
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateTableModal
        open={showCreateTable}
        onClose={() => setShowCreateTable(false)}
        onCreated={() => { setShowCreateTable(false); fetchTables(); }}
      />
      <OpenCardModal
        open={showOpenCard}
        onClose={() => setShowOpenCard(false)}
        onConfirm={handleOpenCard}
        tableNumber={selectedTable?.number}
      />
      <ProductSearchModal
        open={showProductSearch}
        onClose={() => setShowProductSearch(false)}
        cardId={activeCard?.id || ''}
        onItemAdded={() => { if (activeCard) fetchCard(activeCard.id); }}
      />
      <PaymentModal
        open={showPayment}
        onClose={() => setShowPayment(false)}
        card={activeCard}
        onSuccess={() => {
          setShowPayment(false);
          setActiveCard(null);
          setSelectedTable(null);
          fetchTables();
        }}
      />
      <TransferModal
        open={showTransfer}
        onClose={() => setShowTransfer(false)}
        card={activeCard}
        tables={tables}
        onSuccess={() => {
          setShowTransfer(false);
          setActiveCard(null);
          setSelectedTable(null);
          fetchTables();
        }}
      />
      <SplitModal
        open={showSplit}
        onClose={() => setShowSplit(false)}
        card={activeCard}
        onSuccess={() => {
          setShowSplit(false);
          setActiveCard(null);
          setSelectedTable(null);
          fetchTables();
        }}
      />
      {/* Edit Table Modal */}
      {editingTable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Editar Mesa {editingTable.number}</h3>
            <form onSubmit={handleEditTable} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Número</label>
                <input type="number" value={editingTable.number} onChange={(e) => setEditingTable({...editingTable, number: Number(e.target.value)})} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Nome/Label</label>
                <input type="text" value={editingTable.label} onChange={(e) => setEditingTable({...editingTable, label: e.target.value})} placeholder="Ex: Varanda, VIP..." className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Capacidade</label>
                <input type="number" value={editingTable.capacity} onChange={(e) => setEditingTable({...editingTable, capacity: Number(e.target.value)})} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <select value={editingTable.status} onChange={(e) => setEditingTable({...editingTable, status: e.target.value})} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
                  <option value="livre">Livre</option>
                  <option value="reservada">Reservada</option>
                  <option value="inativa">Inativa</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setEditingTable(null)} className="flex-1 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">Cancelar</button>
                <button type="submit" className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Table Confirmation */}
      {deletingTable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl">
            <h3 className="text-lg font-semibold">Excluir Mesa {deletingTable.number}?</h3>
            <p className="text-sm text-muted-foreground mt-2">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setDeletingTable(null)} className="flex-1 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">Cancelar</button>
              <button onClick={handleDeleteTable} className="flex-1 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-white hover:bg-destructive/90">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Active Card Panel ────────────────────────────────────────────────────────

function ActiveCardPanel({
  card,
  onAddItem,
  onRemoveItem,
  onClose,
  onTransfer,
  onSplit,
  onCancel,
  loading,
}: {
  card: ConsumptionCard;
  onAddItem: () => void;
  onRemoveItem: (itemId: string) => void;
  onClose: () => void;
  onTransfer: () => void;
  onSplit: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center flex-1">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-foreground">
              Mesa {card.table_number}
            </h2>
            {card.customer_name && (
              <p className="text-sm text-muted-foreground">{card.customer_name}</p>
            )}
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatTime(card.opened_at)}
            </div>
          </div>
        </div>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-auto p-4 space-y-2">
        {card.items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Nenhum item lançado
          </p>
        ) : (
          card.items.map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-2 rounded-lg bg-muted/30 p-2.5 text-sm"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{item.product_name}</p>
                <p className="text-xs text-muted-foreground">
                  {item.quantity}x {formatCurrency(item.unit_price)}
                </p>
                {item.notes && (
                  <p className="text-xs text-muted-foreground italic mt-0.5">📝 {item.notes}</p>
                )}
              </div>
              <span className="font-medium text-foreground whitespace-nowrap">
                {formatCurrency(item.subtotal)}
              </span>
              <button
                onClick={() => onRemoveItem(item.id)}
                className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
                title="Remover item"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Totals */}
      <div className="border-t border-border p-4 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-foreground">{formatCurrency(Number(card.subtotal))}</span>
        </div>
        {Number(card.discount) > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Desconto</span>
            <span className="text-green-500">-{formatCurrency(Number(card.discount))}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-bold">
          <span className="text-foreground">Total</span>
          <span className="text-foreground">{formatCurrency(Number(card.total))}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-border p-3 space-y-2">
        <Button className="w-full" onClick={onAddItem}>
          <Plus className="h-4 w-4 mr-1" /> Adicionar Item
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={card.items.length === 0}>
            Fechar Conta
          </Button>
          <Button variant="outline" size="sm" onClick={onTransfer}>
            <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Transferir
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={onSplit} disabled={card.items.length < 2}>
            <Split className="h-3.5 w-3.5 mr-1" /> Dividir
          </Button>
          <Button variant="destructive" size="sm" onClick={onCancel}>
            <Ban className="h-3.5 w-3.5 mr-1" /> Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Create Table Modal ───────────────────────────────────────────────────────

function CreateTableModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [number, setNumber] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [capacity, setCapacity] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!number) return;
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/bar/tables', {
        method: 'POST',
        body: JSON.stringify({
          number: parseInt(number),
          label: label || undefined,
          capacity: capacity ? parseInt(capacity) : undefined,
        }),
      });
      if (res.ok) {
        toast.success('Mesa criada com sucesso');
        setNumber('');
        setLabel('');
        setCapacity('');
        onCreated();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao criar mesa');
      }
    } catch {
      toast.error('Erro ao criar mesa');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova Mesa</DialogTitle>
          <DialogDescription>Cadastre uma nova mesa no estabelecimento</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">Número *</label>
            <input
              type="number"
              min="1"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Rótulo</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Varanda, VIP"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Capacidade</label>
            <input
              type="number"
              min="1"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="Número de pessoas"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={submitting || !number}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Criar Mesa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Open Card Modal ──────────────────────────────────────────────────────────

function OpenCardModal({
  open,
  onClose,
  onConfirm,
  tableNumber,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (customerName: string) => void;
  tableNumber?: number;
}) {
  const [customerName, setCustomerName] = React.useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onConfirm(customerName);
    setCustomerName('');
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Abrir Cartão — Mesa {tableNumber}</DialogTitle>
          <DialogDescription>Inicie um novo atendimento nesta mesa</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">Nome do Cliente (opcional)</label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Ex: João Silva"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit">Abrir Cartão</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ─── Product Search Modal ─────────────────────────────────────────────────────

function ProductSearchModal({
  open,
  onClose,
  cardId,
  onItemAdded,
}: {
  open: boolean;
  onClose: () => void;
  cardId: string;
  onItemAdded: () => void;
}) {
  const [search, setSearch] = React.useState('');
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [quantity, setQuantity] = React.useState(1);
  const [notes, setNotes] = React.useState('');
  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(null);
  const [adding, setAdding] = React.useState(false);

  const searchProducts = React.useCallback(async (query: string) => {
    if (!query.trim()) { setProducts([]); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/products?search=${encodeURIComponent(query)}&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.data || data.products || data || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => searchProducts(search), 300);
    return () => clearTimeout(timer);
  }, [search, searchProducts]);

  async function handleAdd() {
    if (!selectedProduct || !cardId) return;
    setAdding(true);
    try {
      const res = await apiFetch(`/api/bar/cards/${cardId}/items`, {
        method: 'POST',
        body: JSON.stringify({
          product_id: selectedProduct.id,
          quantity,
          notes: notes || undefined,
        }),
      });
      if (res.ok) {
        toast.success(`${selectedProduct.name} adicionado`);
        setSelectedProduct(null);
        setQuantity(1);
        setNotes('');
        setSearch('');
        onItemAdded();
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao adicionar item');
      }
    } catch {
      toast.error('Erro ao adicionar item');
    } finally {
      setAdding(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar Produto</DialogTitle>
          <DialogDescription>Busque um produto para lançar no cartão</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou código..."
              className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm"
              autoFocus
            />
          </div>

          {/* Product List */}
          <div className="max-h-48 overflow-auto space-y-1">
            {loading && <p className="text-xs text-muted-foreground text-center py-2">Buscando...</p>}
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedProduct(p)}
                className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                  selectedProduct?.id === p.id
                    ? 'bg-primary/10 border border-primary/30'
                    : 'hover:bg-muted/50'
                }`}
              >
                <span className="text-foreground truncate">{p.name}</span>
                <span className="text-muted-foreground font-medium ml-2">
                  {formatCurrency(Number(p.sale_price))}
                </span>
              </button>
            ))}
          </div>

          {/* Quantity & Notes */}
          {selectedProduct && (
            <div className="space-y-2 border-t border-border pt-3">
              <p className="text-sm font-medium text-foreground">{selectedProduct.name}</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Quantidade</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    className="mt-0.5 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Observação</label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ex: sem gelo"
                    className="mt-0.5 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleAdd} disabled={!selectedProduct || adding || quantity <= 0}>
            {adding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Payment Modal ────────────────────────────────────────────────────────────

function PaymentModal({
  open,
  onClose,
  card,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  card: ConsumptionCard | null;
  onSuccess: () => void;
}) {
  const [payments, setPayments] = React.useState<PaymentEntry[]>([
    { method: 'dinheiro', amount: 0 },
  ]);
  const [discountType, setDiscountType] = React.useState<'percentage' | 'fixed'>('fixed');
  const [discountValue, setDiscountValue] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);

  const subtotal = Number(card?.subtotal || 0);
  const discountAmount =
    discountType === 'percentage'
      ? Math.round((subtotal * discountValue) / 100 * 100) / 100
      : discountValue;
  const finalTotal = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100);
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const change = Math.max(0, Math.round((totalPaid - finalTotal) * 100) / 100);

  function addPayment() {
    setPayments([...payments, { method: 'dinheiro', amount: 0 }]);
  }

  function removePayment(index: number) {
    setPayments(payments.filter((_, i) => i !== index));
  }

  function updatePayment(index: number, field: keyof PaymentEntry, value: unknown) {
    const updated = [...payments];
    updated[index] = { ...updated[index], [field]: value };
    setPayments(updated);
  }

  async function handleSubmit() {
    if (!card) return;
    if (totalPaid < finalTotal) {
      toast.error('Pagamento insuficiente');
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        payments: payments.filter((p) => p.amount > 0).map((p) => ({
          method: p.method,
          amount: p.amount,
          received_amount: p.method === 'dinheiro' ? p.amount : undefined,
        })),
      };
      if (discountValue > 0) {
        body.discount = { type: discountType, value: discountValue };
      }

      const res = await apiFetch(`/api/bar/cards/${card.id}/close`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Conta fechada com sucesso!');
        setPayments([{ method: 'dinheiro', amount: 0 }]);
        setDiscountValue(0);
        onSuccess();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao fechar conta');
      }
    } catch {
      toast.error('Erro ao fechar conta');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Fechar Conta</DialogTitle>
          <DialogDescription>
            Mesa {card?.table_number} — {card?.items.length || 0} itens
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Discount */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Desconto</label>
            <div className="flex gap-2">
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed')}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="fixed">R$ (fixo)</option>
                <option value="percentage">% (percentual)</option>
              </select>
              <input
                type="number"
                min="0"
                step="0.01"
                value={discountValue || ''}
                onChange={(e) => setDiscountValue(Number(e.target.value))}
                className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                placeholder="0"
              />
            </div>
          </div>

          {/* Payments */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Pagamentos</label>
              <button onClick={addPayment} className="text-xs text-primary hover:underline">
                + Adicionar
              </button>
            </div>
            {payments.map((payment, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select
                  value={payment.method}
                  onChange={(e) => updatePayment(i, 'method', e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                >
                  <option value="dinheiro">Dinheiro</option>
                  <option value="cartao_debito">Débito</option>
                  <option value="cartao_credito">Crédito</option>
                  <option value="pix">PIX</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={payment.amount || ''}
                  onChange={(e) => updatePayment(i, 'amount', Number(e.target.value))}
                  className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                  placeholder="Valor"
                />
                {payments.length > 1 && (
                  <button onClick={() => removePayment(i)} className="text-muted-foreground hover:text-destructive">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="rounded-lg bg-muted/30 p-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-green-500">
                <span>Desconto</span>
                <span>-{formatCurrency(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base border-t border-border pt-1">
              <span>Total</span>
              <span>{formatCurrency(finalTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pago</span>
              <span className={totalPaid >= finalTotal ? 'text-green-500' : 'text-red-500'}>
                {formatCurrency(totalPaid)}
              </span>
            </div>
            {change > 0 && (
              <div className="flex justify-between text-yellow-500">
                <span>Troco</span>
                <span>{formatCurrency(change)}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={submitting || totalPaid < finalTotal}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Confirmar Pagamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Transfer Modal ───────────────────────────────────────────────────────────

function TransferModal({
  open,
  onClose,
  card,
  tables,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  card: ConsumptionCard | null;
  tables: Table[];
  onSuccess: () => void;
}) {
  const [selectedTableId, setSelectedTableId] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const freeTables = tables.filter((t) => t.status === 'livre');

  async function handleTransfer() {
    if (!card || !selectedTableId) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/bar/cards/${card.id}/transfer`, {
        method: 'PATCH',
        body: JSON.stringify({ new_table_id: selectedTableId }),
      });
      if (res.ok) {
        toast.success('Mesa transferida com sucesso');
        setSelectedTableId('');
        onSuccess();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao transferir');
      }
    } catch {
      toast.error('Erro ao transferir');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transferir Mesa</DialogTitle>
          <DialogDescription>Selecione a mesa de destino</DialogDescription>
        </DialogHeader>

        {freeTables.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Nenhuma mesa livre disponível
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-2 max-h-48 overflow-auto">
            {freeTables.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTableId(t.id)}
                className={`rounded-lg border-2 p-3 text-center transition-colors ${
                  selectedTableId === t.id
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-lg font-bold">{t.number}</span>
                {t.label && <p className="text-xs text-muted-foreground truncate">{t.label}</p>}
              </button>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleTransfer} disabled={!selectedTableId || submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Transferir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Split Modal ──────────────────────────────────────────────────────────────

function SplitModal({
  open,
  onClose,
  card,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  card: ConsumptionCard | null;
  onSuccess: () => void;
}) {
  const [splitCount, setSplitCount] = React.useState(2);
  const [assignments, setAssignments] = React.useState<Record<string, number>>({});
  const [submitting, setSubmitting] = React.useState(false);

  // Reset assignments when card changes
  React.useEffect(() => {
    if (card) {
      const initial: Record<string, number> = {};
      card.items.forEach((item, i) => {
        initial[item.id] = i % splitCount;
      });
      setAssignments(initial);
    }
  }, [card, splitCount]);

  function assignItem(itemId: string, splitIndex: number) {
    setAssignments((prev) => ({ ...prev, [itemId]: splitIndex }));
  }

  async function handleSplit() {
    if (!card) return;

    // Build splits
    const splits: { item_ids: string[]; payments: PaymentEntry[] }[] = [];
    for (let i = 0; i < splitCount; i++) {
      const itemIds = Object.entries(assignments)
        .filter(([, idx]) => idx === i)
        .map(([id]) => id);
      if (itemIds.length === 0) {
        toast.error(`Divisão ${i + 1} não possui itens`);
        return;
      }
      const splitTotal = card.items
        .filter((item) => itemIds.includes(item.id))
        .reduce((sum, item) => sum + Number(item.subtotal), 0);
      splits.push({
        item_ids: itemIds,
        payments: [{ method: 'dinheiro', amount: Math.round(splitTotal * 100) / 100 }],
      });
    }

    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/bar/cards/${card.id}/split`, {
        method: 'POST',
        body: JSON.stringify({ splits }),
      });
      if (res.ok) {
        toast.success('Conta dividida com sucesso');
        onSuccess();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao dividir conta');
      }
    } catch {
      toast.error('Erro ao dividir conta');
    } finally {
      setSubmitting(false);
    }
  }

  if (!card) return null;

  const splitColors = ['text-blue-400', 'text-green-400', 'text-yellow-400', 'text-purple-400', 'text-pink-400'];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Dividir Conta</DialogTitle>
          <DialogDescription>Atribua cada item a uma pessoa</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">Dividir em:</label>
            <select
              value={splitCount}
              onChange={(e) => setSplitCount(Number(e.target.value))}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            >
              {[2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>{n} pessoas</option>
              ))}
            </select>
          </div>

          <div className="space-y-2 max-h-60 overflow-auto">
            {card.items.map((item) => (
              <div key={item.id} className="flex items-center gap-2 rounded-md bg-muted/30 p-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.product_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.quantity}x — {formatCurrency(item.subtotal)}
                  </p>
                </div>
                <div className="flex gap-1">
                  {Array.from({ length: splitCount }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => assignItem(item.id, i)}
                      className={`h-7 w-7 rounded-full border-2 text-xs font-bold transition-all ${
                        assignments[item.id] === i
                          ? `border-current ${splitColors[i % splitColors.length]} bg-current/10`
                          : 'border-border text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Split totals */}
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: splitCount }, (_, i) => {
              const itemIds = Object.entries(assignments)
                .filter(([, idx]) => idx === i)
                .map(([id]) => id);
              const total = card.items
                .filter((item) => itemIds.includes(item.id))
                .reduce((sum, item) => sum + Number(item.subtotal), 0);
              return (
                <div key={i} className={`rounded-md bg-muted/30 p-2 text-center ${splitColors[i % splitColors.length]}`}>
                  <p className="text-xs">Pessoa {i + 1}</p>
                  <p className="font-bold">{formatCurrency(total)}</p>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSplit} disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Confirmar Divisão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
